import { Box, Button, Skeleton, Stack } from "@mui/material";
import { Player } from "../../../domain/model/User";
import { HeaderView } from "../../component/HeaderView";
import { useTranslation } from "react-i18next";
import { LoadingView } from "../../component/LoadingView";
import { CustomImageView } from "../../component/CustomImageView";
import {
  APP_ICON_IMAGE,
  APP_ICON_IMAGE_WITHOUT_TITLE,
  STORAGE_KEYS,
} from "../../../data/constant/Constants";
import {
  COLOR_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_VERY_LIGHT_GRAY,
  COLOR_WHITE,
} from "../../../data/colors/Colors";
import useWindowDimensions from "../../../core/utils/UseWindowDimensions";
import { CustomButtonView } from "../../component/CustomButtonView";
import { CustomPopoverView } from "../../component/CustomPopoverView";
import { useEffect, useState } from "react";
import { ChoosePackageView } from "../choosePackage/ChoosePackageView";
import { NewGameView } from "../newGame/NewGameView";
import { CustomTextView } from "../../component/CustomTextView";
import { GamesRounded, HistoryRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import SharedPreferences from "../../../core/utils/SharedPreferences";
import { ROUTE_ENDPOINTS } from "../../../data/constant/RouteConstants";
import { openLink } from "../../../core/utils/Utils";

export function PlayView({ player }: { player: Player | undefined }) {
  const { t } = useTranslation();

  const { width, height } = useWindowDimensions();
  const isMobile = width < 700 || height < 700;

  const [anchorElPosition, setAnchorElPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const [clickType, setClickType] = useState(1);
  const navigate = useNavigate();

  const [popoverContentHeight, setPopoverContentHeight] = useState("auto");

  const [isGlowing, setIsGlowing] = useState(true);

  const noGamesToPlay = player?.gamesLeftToPlay! <= 0;

  useEffect(() => {
    if (anchorElPosition != null) {
      setIsGlowing(false);
    } else {
      setTimeout(() => {
        setIsGlowing(true);
      }, 500);
    }
  }, [anchorElPosition]);

  return (
    <Stack>
      <HeaderView title={t("play")} />

      {!player ? (
        <LoadingView />
      ) : (
        <Stack
          width={"100%"}
          minHeight={"90vh"}
          p={isMobile ? 1 : 4}
          justifyContent={"space-between"}
          alignItems={"center"}
          spacing={2}
        >
          <Stack
            width={isMobile ? (width / height > 2 ? "50vw" : "80vw") : "30vw"}
            alignItems={"center"}
            spacing={1}
          >
            <CustomImageView
              src={require("../../../assets/images/marron_writing_logo.png")}
              width={"100%"}
              height={"30vh"}
              scale="contain"
              props={{
                borderRadius: 4,
                border: "solid 2px " + COLOR_MAIN,
                p: 0,
              }}
            />
            <Stack width={"100%"}>
              <CustomButtonView
                text={t("start_new_game")}
                fontSize="h6"
                props={{ pt: 0.5, pb: 0.5 }}
                isGlowing={isGlowing}
                glowingColor="rgba(116, 5, 47, 0.8)"
                onClick={(e) => {
                  setClickType(1);
                  setAnchorElPosition({
                    top: e.clientY,
                    left: e.clientX,
                  });
                }}
              />
            </Stack>

            <Stack width={"100%"} alignItems={"center"} pt={2}>
              <CustomTextView
                text={t("home_text_description")}
                variant="h6"
                textColor={COLOR_MAIN}
              />
              <Stack
                direction={"row"}
                alignItems={"center"}
                spacing={1}
                sx={{ direction: "ltr" }}
              >
                <CustomImageView
                  width={40}
                  height={40}
                  src={require("../../../assets/images/instagram_logo.png")}
                />
                <Button
                  onClick={() =>
                    openLink("https://instagram.com/ellytbeeh.qa", "_blank")
                  }
                  sx={{ textTransform: "none", p: 0, borderRadius: 12 }}
                >
                  <CustomTextView
                    text={"@ellytbeeh.qa"}
                    variant="h6"
                    fontWeight="bold"
                    textColor={COLOR_MAIN}
                  />
                </Button>
              </Stack>
            </Stack>
          </Stack>

          <Stack
            width={isMobile ? (width / height > 2 ? "50vw" : "80vw") : "30vw"}
            gap={3}
          >
            <Stack width={"100%"} alignItems={"center"}>
              <CustomTextView
                text={player.gamesLeftToPlay?.toString()}
                fontWeight="bold"
                variant="h1"
                textAlign="center"
              />
              <CustomTextView
                text={t("games_left_to_play_")}
                textColor={COLOR_LIGHT_GRAY}
                variant="h6"
                textAlign="center"
              />
            </Stack>
            <Stack gap={1}>
              <Button
                sx={{
                  borderRadius: 12,
                  background: COLOR_BLACK,
                  border: "solid 1px " + COLOR_BLACK,
                  textTransform: "none",
                }}
                onClick={(e) => {
                  if (
                    SharedPreferences.getBoolean(
                      STORAGE_KEYS.LOGGED_IN_AS_GUEST
                    ) === true
                  ) {
                    alert("You need to signup");
                    return;
                  }
                  setClickType(2);
                  setAnchorElPosition({
                    top: e.clientY,
                    left: e.clientX,
                  });
                }}
              >
                <Stack direction={"row"} alignItems={"center"} gap={1}>
                  <CustomTextView
                    text={t("buy_more_games")}
                    textColor={COLOR_WHITE}
                    fontWeight="bold"
                    textAlign="center"
                    variant="h6"
                  />
                  <GamesRounded sx={{ color: COLOR_WHITE }} />
                </Stack>
              </Button>
              <Button
                sx={{
                  borderRadius: 12,
                  border: "solid 1px " + COLOR_BLACK,
                  textTransform: "none",
                }}
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate(ROUTE_ENDPOINTS.HOME_PLAYED_GAMES);
                }}
              >
                <Stack direction={"row"} alignItems={"center"} gap={1}>
                  <CustomTextView
                    text={t("see_games_history")}
                    textColor={COLOR_BLACK}
                    textAlign="center"
                    variant="h6"
                  />
                  <HistoryRounded sx={{ color: COLOR_BLACK }} />
                </Stack>
              </Button>
            </Stack>
          </Stack>
          <Box />
        </Stack>
      )}

      <CustomPopoverView
        onClose={() => {
          setAnchorElPosition(null);
        }}
        anchorElPosition={anchorElPosition}
        width={
          isMobile
            ? width
            : isMobile
            ? width / height > 2
              ? "100%"
              : "30vw"
            : "28vw"
        }
        height={
          noGamesToPlay || clickType == 2
            ? "100%"
            : isMobile
            ? width / height > 2
              ? "100%"
              : popoverContentHeight
            : popoverContentHeight
        }
        layout={
          <>
            {player && (
              <>
                {clickType == 1 && !noGamesToPlay && (
                  <>
                    <NewGameView
                      handleClose={() => setAnchorElPosition(null)}
                      handleHeightChanged={(h) => {
                        setPopoverContentHeight(h);
                      }}
                      player={player}
                      shouldChooseCategoryDirectly={
                        player.usedFreeTrial == false &&
                        player.freeTrialEnabled == true
                      }
                    />
                  </>
                )}
                {(clickType == 2 || noGamesToPlay) && (
                  <ChoosePackageView
                    player={player}
                    handleClose={() => setAnchorElPosition(null)}
                  />
                )}
              </>
            )}
          </>
        }
      />
    </Stack>
  );
}
